// src/contexts/DashboardContext.js
import React, { createContext, useState, useContext } from 'react';

// Create context
const DashboardContext = createContext();

// Custom hook to use the dashboard context
export const useDashboardContext = () => useContext(DashboardContext);

// Provider component
export const DashboardProvider = ({ children }) => {
  // State to store cached data
  const [cachedFinancialData, setCachedFinancialData] = useState({});
  
  // Store periods data with progress tracking
  const [cachedPeriodsData, setCachedPeriodsData] = useState({
    initialLoaded: false,    // First 3 periods loaded
    fullyLoaded: false,      // All periods loaded
    allPeriods: [],          // All period data
    loadedPeriodIds: new Set() // Track which period IDs are loaded
  });
  
  // Check if data for a specific period is cached
  const isPeriodDataCached = (period) => {
    return cachedFinancialData[period] !== undefined;
  };
  
  // Store financial data in cache
  const cacheFinancialData = (period, data) => {
    setCachedFinancialData(prev => ({
      ...prev,
      [period]: data
    }));
  };
  
  // Add initial periods data (first 3)
  const cacheInitialPeriods = (periodsData) => {
    const periodIds = new Set(periodsData.map(p => p.period));
    
    setCachedPeriodsData(prev => ({
      ...prev,
      initialLoaded: true,
      allPeriods: periodsData,
      loadedPeriodIds: periodIds
    }));
  };
  
  // Add additional periods data
  const cacheAdditionalPeriods = (periodsData) => {
    // Create a map of existing periods by ID for quick lookup
    const existingPeriodsMap = new Map(
      cachedPeriodsData.allPeriods.map(p => [p.period, p])
    );
    
    // Add new periods to the map
    for (const period of periodsData) {
      existingPeriodsMap.set(period.period, period);
    }
    
    // Convert map back to array and sort by period ID
    const combinedPeriods = Array.from(existingPeriodsMap.values())
      .sort((a, b) => b.period.localeCompare(a.period));
    
    // Update the Set of loaded period IDs
    const newLoadedIds = new Set(cachedPeriodsData.loadedPeriodIds);
    periodsData.forEach(p => newLoadedIds.add(p.period));
    
    setCachedPeriodsData(prev => ({
      ...prev,
      allPeriods: combinedPeriods,
      fullyLoaded: true,
      loadedPeriodIds: newLoadedIds
    }));
  };
  
  // Mark as fully loaded (when there are no more periods to load)
  const markAllPeriodsLoaded = () => {
    setCachedPeriodsData(prev => ({
      ...prev,
      fullyLoaded: true
    }));
  };
  
  // Get a filtered subset of periods based on count
  const getFilteredPeriods = (count) => {
    if (!cachedPeriodsData.initialLoaded || cachedPeriodsData.allPeriods.length === 0) {
      return [];
    }
    
    // If count is 0, return all loaded periods
    if (count === 0) {
      return cachedPeriodsData.allPeriods;
    }
    
    // Otherwise return the specified number of periods
    return cachedPeriodsData.allPeriods.slice(0, Math.min(count, cachedPeriodsData.allPeriods.length));
  };
  
  // Check loading status
  const getLoadingStatus = () => {
    return {
      initialLoaded: cachedPeriodsData.initialLoaded,
      fullyLoaded: cachedPeriodsData.fullyLoaded,
      loadedCount: cachedPeriodsData.allPeriods.length,
      loadedPeriodIds: cachedPeriodsData.loadedPeriodIds
    };
  };
  
  // Clear cache (useful for forcing refresh)
  const clearCache = () => {
    setCachedFinancialData({});
    setCachedPeriodsData({
      initialLoaded: false,
      fullyLoaded: false,
      allPeriods: [],
      loadedPeriodIds: new Set()
    });
  };
  
  const value = {
    cachedFinancialData,
    cachedPeriodsData,
    isPeriodDataCached,
    cacheFinancialData,
    cacheInitialPeriods,
    cacheAdditionalPeriods,
    markAllPeriodsLoaded,
    getFilteredPeriods,
    getLoadingStatus,
    clearCache
  };
  
  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};